import "./ICDCoding.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import KaboodleToaster from "components/common/KaboodleToaster";
import type { OptionType } from "components/inputs";
import { Select } from "components/inputs";
import Tooltip from "components/Tooltip/Tooltip";
import debounce from "lodash/debounce";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import type { MultiValue, SingleValue } from "react-select";
import { useGetICDCodesByInputQuery } from "services/codes-api";
import type { ConstantTypes, ICDCode } from "types/DataTypes";
import { useToken } from "utils/context";
import { useSelector } from "utils/redux";

import type { ICDObject } from "./icdAdapter";
import { MAX_ICD_CODES } from "./ICDCodingBlock";
import ICDCodingItem from "./ICDCodingItem";

type ICDCodingFormProps = {
  data: ICDObject;
  onSave: (field: string | null, value: unknown) => Promise<void>;
  readonly: boolean;
  icdCodes: string[];
  onRemoveCode: (codeName: string) => void;
  canAddNewCode?: boolean;
  hasIcdData?: boolean;
  onAddCode?: () => void;
  onShowResetModal?: () => void;
  showAcceptanceButtons?: boolean;
  savingFields: Record<string, boolean>;
};

type FormValues = {
  icd_indicator: string | null;
  alleged_cause_of_injury: string;
};

type SelectChangeValue = MultiValue<OptionType> | SingleValue<OptionType>;

const ICDHeading = memo(
  ({
    control,
    constants,
    icdCodes,
    errors,
    onSave,
    getIndicatorValue,
    getValues,
    setValue,
    readonly,
    data,
    canAddNewCode,
    hasIcdData,
    onAddCode,
    onShowResetModal,
    savingFields,
  }: {
    control: Control<FormValues>;
    constants: ConstantTypes;
    icdCodes: string[];
    errors: FieldErrors<FormValues>;
    onSave: (field: string | null, value: unknown) => Promise<void>;
    getIndicatorValue: (value: string | null) => OptionType | undefined;
    getValues: UseFormGetValues<FormValues>;
    setValue: UseFormSetValue<FormValues>;
    readonly: boolean;
    data: ICDObject;
    canAddNewCode?: boolean;
    hasIcdData?: boolean;
    onAddCode?: () => void;
    onShowResetModal?: () => void;
    savingFields: Record<string, boolean>;
  }) => {
    const { accessToken } = useToken();
    const [causeSearchTerm, setCauseSearchTerm] = useState<string | null>(null);
    const [optimisticCauseOption, setOptimisticCauseOption] =
      useState<OptionType | null>(null);

    const debouncedSetCauseSearchTerm = useMemo(
      () => debounce((value: string | null) => setCauseSearchTerm(value), 300),
      [],
    );

    // ICD-9 external cause codes all start with "E" — default to that prefix so
    // options appear without the user having to type. ICD-10 uses V/W/X/Y.
    const effectiveCauseSearchTerm = useMemo(() => {
      if (!data?.icd_indicator) return null;
      return data.icd_indicator === "9"
        ? causeSearchTerm || "E"
        : causeSearchTerm;
    }, [data?.icd_indicator, causeSearchTerm]);

    const { data: causeResults, isFetching: isCauseFetching } =
      useGetICDCodesByInputQuery(
        {
          icd_indicator: data?.icd_indicator,
          code__istartswith: effectiveCauseSearchTerm,
          limit: 25,
        },
        {
          skip:
            !accessToken || !effectiveCauseSearchTerm || !data?.icd_indicator,
        },
      );

    const causeOptions = useMemo(
      () =>
        causeResults?.results?.map((result: ICDCode) => ({
          label: `${result.code} - ${result.description}`,
          value: result.code,
        })) ?? [],
      [causeResults],
    );

    const handleIndicatorChange = useCallback(
      async (newValue: OptionType | null) => {
        const prevIndicator = getValues("icd_indicator");
        const prevCause = getValues("alleged_cause_of_injury");
        const newIndicator =
          newValue?.value === null || newValue?.value === undefined
            ? null
            : String(newValue.value);

        setValue("icd_indicator", newIndicator);
        setValue("alleged_cause_of_injury", "");
        setOptimisticCauseOption(null);

        const updatePayload = {
          icd_indicator: newIndicator,
          alleged_cause_of_injury: null,
        };

        try {
          if (icdCodes?.length > 0) {
            const clearCodes = Object.fromEntries(
              icdCodes.flatMap((codeName) => [
                [codeName, null],
                [`${codeName}_verbose`, null],
              ]),
            );

            await onSave(null, {
              ...updatePayload,
              ...clearCodes,
            });
            KaboodleToaster.success("ICD indicator updated and codes cleared");
          } else {
            await onSave(null, updatePayload);
            KaboodleToaster.success("ICD indicator updated");
          }
        } catch {
          setValue("icd_indicator", prevIndicator);
          setValue("alleged_cause_of_injury", prevCause);
        }
      },
      [getValues, icdCodes, onSave, setValue],
    );

    const isIndicatorSaving = Boolean(savingFields.icd_indicator);
    const isCauseSaving = Boolean(savingFields.alleged_cause_of_injury);

    const getCauseSelectValue = useCallback(
      (code: string) => {
        if (!code) {
          return null;
        }

        if (
          optimisticCauseOption &&
          optimisticCauseOption.value?.toString() === code
        ) {
          return optimisticCauseOption;
        }

        return (
          causeOptions.find((o) => o.value === code) ?? {
            label: code,
            value: code,
          }
        );
      },
      [causeOptions, optimisticCauseOption],
    );

    return (
      <div className="data-block">
        <div className="flex w-100 form">
          <div className="card__children icd-heading-field icd-heading-field--left">
            <Controller
              control={control}
              name="icd_indicator"
              render={({ field: { value } }) => (
                <Select
                  onChange={(newValue: SelectChangeValue) => {
                    void handleIndicatorChange(newValue as OptionType | null);
                  }}
                  name="icd_indicator"
                  value={getIndicatorValue(value)}
                  options={constants?.["ICD_INDICATORS"]}
                  label="ICD Indicator"
                  errors={errors}
                  readOnly={readonly || isIndicatorSaving}
                  widthVariant="full"
                />
              )}
            />
          </div>
          <div className="card__children icd-heading-field">
            <Controller
              control={control}
              name="alleged_cause_of_injury"
              render={({ field: { value, onChange } }) => (
                <Select
                  name="alleged_cause_of_injury"
                  label="ICD Cause"
                  widthVariant="full"
                  value={getCauseSelectValue(value)}
                  options={causeOptions}
                  placeholder={
                    !data?.icd_indicator
                      ? "Set indicator first"
                      : data.icd_indicator === "9"
                        ? "Search E-codes..."
                        : "Type V, W, X, or Y..."
                  }
                  noOptionsMessage={({ inputValue }) => {
                    if (!data?.icd_indicator) return "Set indicator first";
                    if (!inputValue && data.icd_indicator !== "9")
                      return "Type V, W, X, or Y to search...";
                    return isCauseFetching
                      ? "Searching..."
                      : "No matches found.";
                  }}
                  onInputChange={(inputValue: string) =>
                    debouncedSetCauseSearchTerm(inputValue || null)
                  }
                  onChange={(option: OptionType | null) => {
                    void (async () => {
                      const previousValue = value;
                      const previousOptimisticOption = optimisticCauseOption;
                      const code = option?.value?.toString() ?? null;

                      setOptimisticCauseOption(option);
                      onChange(code ?? "");

                      try {
                        await onSave("alleged_cause_of_injury", code);
                      } catch {
                        onChange(previousValue ?? "");
                        setOptimisticCauseOption(previousOptimisticOption);
                      }
                    })();
                  }}
                  readOnly={readonly || !data?.icd_indicator || isCauseSaving}
                  isClearable
                  errors={errors}
                />
              )}
            />
          </div>
          {onAddCode && onShowResetModal && (
            <div className="flex jc-end ai-c">
              <Tooltip
                content={
                  !canAddNewCode
                    ? "Please set ICD indicator first"
                    : MAX_ICD_CODES === icdCodes.length
                      ? `Maximum of ${MAX_ICD_CODES} codes allowed`
                      : "Add new ICD code"
                }
                variant={
                  !canAddNewCode || MAX_ICD_CODES === icdCodes.length
                    ? "warning"
                    : "dark"
                }
              >
                <ButtonIcon
                  kind="neutral"
                  size="small"
                  onClick={onAddCode}
                  disabled={!canAddNewCode || MAX_ICD_CODES === icdCodes.length}
                >
                  add_circle
                </ButtonIcon>
              </Tooltip>
              <Tooltip
                content={hasIcdData ? "Reset ICD data" : "No ICD data to reset"}
                variant="dark"
                place="right"
              >
                <ButtonIcon
                  kind="neutral"
                  size="small"
                  onClick={onShowResetModal}
                  data-testid="reset-icd-codes"
                  disabled={!hasIcdData}
                >
                  delete
                </ButtonIcon>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    );
  },
);

ICDHeading.displayName = "ICDHeading";

const ICDCodingForm = ({
  data,
  onSave,
  readonly,
  icdCodes,
  onRemoveCode,
  canAddNewCode,
  hasIcdData,
  onAddCode,
  onShowResetModal,
  showAcceptanceButtons = true,
  savingFields,
}: ICDCodingFormProps) => {
  const constants = useSelector((state) => state.shared.constants);

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      icd_indicator: null,
      alleged_cause_of_injury: data?.alleged_cause_of_injury || "",
    },
  });

  useEffect(() => {
    if (savingFields.icd_indicator || savingFields.alleged_cause_of_injury) {
      return;
    }

    reset({
      icd_indicator: data?.icd_indicator,
      alleged_cause_of_injury: data?.alleged_cause_of_injury || "",
    });
  }, [
    data?.icd_indicator,
    data?.alleged_cause_of_injury,
    reset,
    savingFields.icd_indicator,
    savingFields.alleged_cause_of_injury,
  ]);

  const getIndicatorValue = (value: string | null) => {
    const foundOption = constants?.["ICD_INDICATORS"]?.find(
      (opt: ConstantTypes["ICD_INDICATORS"][number]) =>
        opt.value === value || (value === null && opt.value === null),
    );
    return foundOption;
  };

  return (
    <>
      <ICDHeading
        {...{
          control,
          constants,
          icdCodes,
          errors,
          onSave,
          getIndicatorValue,
          getValues,
          setValue,
          readonly,
          data,
          canAddNewCode,
          hasIcdData,
          onAddCode,
          onShowResetModal,
          savingFields,
        }}
      />
      <div className="icd-codes-masonry">
        {icdCodes?.map((codeName) => {
          const codeNumber = parseInt(codeName.split("_").pop() || "0");
          return (
            <ICDCodingItem
              key={codeName}
              codeNumber={codeNumber}
              icdCodeName={codeName}
              data={data}
              onSave={onSave}
              onRemove={onRemoveCode}
              showAcceptanceButtons={showAcceptanceButtons}
              isSaving={
                Boolean(savingFields[codeName]) ||
                Boolean(savingFields[`${codeName}_accepted`])
              }
            />
          );
        })}
      </div>
      <br />
    </>
  );
};

export default ICDCodingForm;
